import { SearchRootDirectoryText, SearchText } from './FilePickerPane.locstring.json';
import loc, { format } from 'owa-localize';
import * as React from 'react';
import AttachmentDataProviderType from 'owa-service/lib/contract/AttachmentDataProviderType';
import AttachmentItemsSortColumn from 'owa-service/lib/contract/AttachmentItemsSortColumn';
import changeListViewItemsSorting from '../actions/changeListViewItemsSorting';
import changeSelectedFolder from '../actions/changeSelectedFolder';
import closeFilePicker from '../actions/closeFilePicker';
import exitSearch from '../actions/search/exitSearch';
import FilePickerErrorMessageView from './FilePickerErrorMessageView';
import FilePickerGroupsView from './GroupsView/FilePickerGroupsView';
import FilePickerListView from './FilePickerListView';
import FilePickerPaneBottomPart from './FilePickerPaneBottomPart';
import FilePickerPanelViewState from '../store/schema/FilePickerPanelViewState';
import FilePickerPaneTopPart, { SearchBoxProps } from './FilePickerPaneTopPart';
import FilePickerTileView from './TileView/FilePickerTileView';
import loadMoreItemsForSelectedProvider from '../actions/loadMoreItemsForSelectedProvider';
import OneDriveProScope from 'owa-service/lib/contract/OneDriveProScope';
import OneDriveProScopeType from 'owa-service/lib/contract/OneDriveProScopeType';
import popFilePickerBreadcrumbsTill from '../actions/popFilePickerBreadcrumbsTill';
import setBreadCrumbsForSearch from '../actions/search/setBreadCrumbsForSearch';
import startSearch from '../actions/search/startSearch';

import updateFileProviderItemsSelection from '../actions/updateFileProviderItemsSelection';
import { FilePickerAction } from 'owa-filepicker';
import { FilePickerBreadcrumb } from '../store/schema/FilePickerViewState';
import { FilePickerViewType } from '../types/FilePickerViewSwitcher';

import { getFileProviderInfo } from '../utils/fileProviderInfo';
import { observer } from 'mobx-react';
import FileProviderItemViewState, {
    FileProviderItemType,
    FolderItemViewState,
} from '../store/schema/FileProviderItemViewState';
import { assertNever } from 'owa-assert';

const classNames = require('classnames');
const styles = require('./FilePickerPane.scss');

export interface FilePickerPaneProps extends React.HTMLProps<HTMLDivElement> {
    viewState: FilePickerPanelViewState;
    onFilesSelected: (files: FileProviderItemViewState[]) => void;
    maxAllowedLocalFilesSize?: number;
    selectedView: FilePickerViewType;
    isTopLevelGroupsView: boolean;
}

@observer
export default class FilePickerPane extends React.Component<FilePickerPaneProps, {}> {
    render() {
        const {
            viewState: { filePickerViewState },
        } = this.props;

        const { breadcrumbs, isLoadingItems, supportedActions } = filePickerViewState;

        const currentSupportedActions = this.getCurrentSupportedActions(supportedActions);

        const searchLabelName = filePickerViewState.selectedFolder
            ? filePickerViewState.selectedFolder.name
            : filePickerViewState.breadcrumbs
            ? filePickerViewState.breadcrumbs[0].name
            : loc(SearchRootDirectoryText);

        const searchLabelText = format(loc(SearchText), searchLabelName);

        const isUsing3S =
            this.props.viewState.selectedFileProvider.model.Type ===
            AttachmentDataProviderType.MailMessage;

        const searchBoxProps: SearchBoxProps = {
            isSearchEnabled: this.isSearchEnabled(),
            onSearch: this.onSearch,
            onExitSearch: this.onExitSearch,
            labelText: searchLabelText,
            currentSearchText: filePickerViewState.searchViewState.searchQuery,
            isUsing3S: isUsing3S,
        };

        return (
            <div className={classNames([this.props.className, styles.container])}>
                <FilePickerPaneTopPart
                    breadcrumbs={breadcrumbs}
                    onBreadcrumbSelected={this.onBreadcrumbSelected}
                    spinnerEnabled={isLoadingItems}
                    searchBoxProps={searchBoxProps}
                />

                {this.getFilePickerItemView()}

                <FilePickerPaneBottomPart
                    supportedActions={currentSupportedActions}
                    onNextClicked={this.onNextClicked}
                    onCancelClicked={this.onCancelClicked}
                />
            </div>
        );
    }

    private getCurrentSupportedActions = (
        supportedActions: FilePickerAction[]
    ): FilePickerAction[] => {
        if (this.props.isTopLevelGroupsView) {
            return [];
        } else {
            return supportedActions;
        }
    };

    private getFilePickerItemView = (): JSX.Element => {
        if (!this.props.viewState.selectedFileProvider) {
            // If there is no selected file provider then we render an empty view
            return <span className={styles.emptyFilePickerView} />;
        }

        const {
            selectedFileProvider,
            filePickerErrorMessageViewState,
            filePickerViewState: {
                selectedFolder,
                selectedFileProviderItems,
                fileProviderItems,
                isLoadingItems,
                columnSorting: attachmentItemsSort,
            },
        } = this.props.viewState;

        // The scope SharedWithMe is treated differently for what columns we show and how we sort columns compared to other ODB scopes.
        // However, in SharedWithMe if the user opens a folder we use our normal ODB columns/sorting since these getItems calls don't go through
        // the SharedWithMe API. So we only want to treat the SharedWithMeScope as special if selectedFolder is null.
        const useScopeForColumnInfo: boolean = !selectedFolder;

        const fileProviderScope = this.props.viewState.selectedProviderScope;
        const fileProviderInfo = getFileProviderInfo(selectedFileProvider.type);
        let getFilePickerListColumnInfo = fileProviderInfo.getFilePickerListColumnInfo;
        if (
            useScopeForColumnInfo &&
            selectedFileProvider.type === AttachmentDataProviderType.OneDrivePro
        ) {
            const oneDriveProScope: OneDriveProScope = fileProviderScope as OneDriveProScope;
            if (oneDriveProScope.Type === OneDriveProScopeType.SharedWithMe) {
                getFilePickerListColumnInfo = (name: string) =>
                    fileProviderInfo.getFilePickerListColumnInfo(name, true);
            }
        }
        const dataSourceId = (selectedFolder && selectedFolder.key) || 'root';

        if (filePickerErrorMessageViewState.isShown) {
            return <FilePickerErrorMessageView message={filePickerErrorMessageViewState.message} />;
        }

        switch (this.props.selectedView) {
            case FilePickerViewType.List:
                return (
                    <FilePickerListView
                        dataSourceId={dataSourceId}
                        getItemToRender={fileProviderInfo.getColumnRenderFunction(
                            useScopeForColumnInfo ? fileProviderScope : null
                        )}
                        items={fileProviderItems}
                        selectedItems={selectedFileProviderItems}
                        onLoadMoreRows={this.onLoadMoreRows}
                        isLoadRowsInProgress={
                            this.props.viewState.filePickerViewState.isLoadingItems
                        }
                        getCanLoadMore={this.getCanLoadMore}
                        getFilePickerListColumnInfo={getFilePickerListColumnInfo}
                        currentSorting={attachmentItemsSort}
                        isAnyItemSelected={selectedFileProviderItems.length > 0}
                        onItemSelected={this.onItemSelected}
                        onItemsSelectionUpdated={this.onItemsSelectionUpdated}
                        sortByColumn={this.sortByColumn}
                    />
                );
            case FilePickerViewType.Tile:
                return (
                    <FilePickerTileView
                        dataSourceId={dataSourceId}
                        items={fileProviderItems}
                        selectedItems={selectedFileProviderItems}
                        onLoadMoreRows={this.onLoadMoreRows}
                        isLoadRowsInProgress={isLoadingItems}
                        getCanLoadMore={this.getCanLoadMore}
                        onItemSelected={this.onItemSelected}
                        onItemsSelectionUpdated={this.onItemsSelectionUpdated}
                    />
                );
            case FilePickerViewType.Groups:
                return (
                    <FilePickerGroupsView
                        dataSourceId={dataSourceId}
                        items={fileProviderItems}
                        selectedItems={selectedFileProviderItems}
                        onLoadMoreRows={this.onLoadMoreRows}
                        isLoadRowsInProgress={isLoadingItems}
                        getCanLoadMore={this.getCanLoadMore}
                        onItemSelected={this.onItemSelected}
                        onItemsSelectionUpdated={this.onItemsSelectionUpdated}
                    />
                );
            default:
                assertNever(this.props.selectedView);
        }

        throw new Error(`Unrecognized view type ${this.props.selectedView}`);
    };

    private onLoadMoreRows = () => {
        loadMoreItemsForSelectedProvider(this.props.viewState);
    };

    private getCanLoadMore = () => {
        const filePickerViewState = this.props.viewState.filePickerViewState;
        return (
            filePickerViewState &&
            filePickerViewState.fileProviderItems.length < filePickerViewState.totalItemsCount
        );
    };

    private onNextClicked = () => {
        if (this.props.viewState.filePickerViewState.selectedFileProviderItems.length === 0) {
            // NOOP if no files are selected. It shouldn't even happen because next should be disabled in this case.
            return;
        }

        this.props.onFilesSelected(
            this.props.viewState.filePickerViewState.selectedFileProviderItems
        );
    };

    private onItemsSelectionUpdated = (selectedItems: FileProviderItemViewState[]) => {
        const {
            maxAllowedLocalFilesSize,
            viewState: { selectedFileProvider, filePickerViewState },
        } = this.props;

        updateFileProviderItemsSelection(
            selectedItems,
            maxAllowedLocalFilesSize,
            selectedFileProvider,
            filePickerViewState
        );
    };

    private onItemSelected = (item: FileProviderItemViewState) => {
        if (item.type === FileProviderItemType.Folder || item.type === FileProviderItemType.Group) {
            const folder = item as FolderItemViewState;

            // If we open a folder from search we do not know if it is a child of the current folder or not.
            // We clear all folder bread crumbs, and add "..." to the scope breadscrumb in this case to avoid potentially showing an
            // incorrect breadcrumb chain.
            if (this.props.viewState.filePickerViewState.searchViewState.isInSearchMode) {
                setBreadCrumbsForSearch(
                    this.props.viewState.selectedFileProvider,
                    this.props.viewState.selectedProviderScope,
                    this.props.viewState.filePickerViewState
                );
            }
            this.loadFolder(folder);
        } else if (item.type === FileProviderItemType.File) {
            // If a file is selected then we go with the same flow as clicking Next
            this.onNextClicked();
        }
    };

    private loadFolder = (folder: FolderItemViewState) => {
        changeSelectedFolder(folder, this.props.viewState);
    };

    private sortByColumn = (
        sortColumn: AttachmentItemsSortColumn,
        customSort?: (
            items: FileProviderItemViewState[],
            sortAsc: boolean
        ) => FileProviderItemViewState[]
    ) => {
        changeListViewItemsSorting(sortColumn, this.props.viewState, customSort);
    };

    private onBreadcrumbSelected = (breadcrumb: FilePickerBreadcrumb) => {
        popFilePickerBreadcrumbsTill(breadcrumb, this.props.viewState.filePickerViewState);
        this.loadFolder(breadcrumb.associatedItem);
    };

    private isSearchEnabled() {
        const { selectedFileProvider, selectedProviderScope } = this.props.viewState;

        if (!selectedFileProvider) {
            return false;
        }

        if (selectedFileProvider.type === AttachmentDataProviderType.OneDrivePro) {
            const oDPScope: OneDriveProScope = selectedProviderScope;
            return oDPScope.Type === OneDriveProScopeType.MyDocuments;
        }

        return (
            selectedFileProvider.model.IsSearchSupported &&
            selectedFileProvider.model.Scopes[selectedFileProvider.model.DefaultScopeIndex] ===
                selectedProviderScope
        );
    }

    private onSearch = (searchQuery: string) => {
        startSearch(this.props.viewState, searchQuery);
    };

    private onExitSearch = () => {
        exitSearch(this.props.viewState);
    };

    private onCancelClicked = () => {
        closeFilePicker(this.props.maxAllowedLocalFilesSize, this.props.viewState);
    };
}
